import React, { useState, useRef, useEffect, useCallback } from "react";
import "../styles/gateway/combo-input.css";

/**
 * Text input with dropdown suggestions. Users can type freely or pick from the list.
 *
 * @param {string} value - Current value
 * @param {function} onChange - (value: string) => void
 * @param {Array<{value:string, label:string, group?:string}>} options - Suggestions
 * @param {string} [placeholder]
 * @param {string} [className]
 */
export default function ComboInput({ value, onChange, options = [], placeholder = "", className = "", ariaLabel = "" }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const query = (filter || value || "").toLowerCase();
  const filtered = query
    ? options.filter((o) =>
        o.value.toLowerCase().includes(query) ||
        o.label.toLowerCase().includes(query) ||
        (o.group || "").toLowerCase().includes(query)
      )
    : options;

  const positionMenu = useCallback(() => {
    const input = inputRef.current;
    const menu = menuRef.current;
    if (!input || !menu) return;
    const r = input.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const fitsBelow = window.innerHeight - r.bottom - 4 >= menuH;
    menu.style.position = "fixed";
    menu.style.top = fitsBelow ? `${Math.round(r.bottom + 4)}px` : `${Math.round(r.top - menuH - 4)}px`;
    menu.style.left = `${Math.round(r.left)}px`;
    menu.style.width = `${Math.round(r.width)}px`;
  }, []);

  const menuCallbackRef = useCallback(
    (node) => {
      menuRef.current = node;
      if (node) positionMenu();
    },
    [positionMenu]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, positionMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
      setFilter("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setFilter(v);
    onChange(v);
    if (!open) setOpen(true);
  };

  const pick = (opt) => {
    onChange(opt.value);
    setFilter("");
    setOpen(false);
    inputRef.current?.focus();
  };

  // Group options
  const groups = {};
  for (const o of filtered) {
    const g = o.group || "";
    if (!groups[g]) groups[g] = [];
    groups[g].push(o);
  }

  return (
    <div className={`gwComboWrap ${className}`} ref={wrapRef}>
      <input
        ref={inputRef}
        className="gwInput gwComboInput"
        value={filter || value}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        aria-label={ariaLabel || undefined}
        autoComplete="off"
        spellCheck={false}
      />
      {open && filtered.length > 0 && (
        <div ref={menuCallbackRef} className="gwComboMenu">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              {group && <div className="gwComboGroup">{group}</div>}
              {items.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`gwComboOption ${o.value === value ? "isActive" : ""}`}
                  onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                >
                  <span className="gwComboOptionLabel">{o.label}</span>
                  <span className="gwComboOptionValue">{o.value}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
